// File: src/pages/Categories.jsx

import React, { useEffect, useState } from "react";
import { styled } from '@mui/material/styles';
import AdminLayout from "../components/AdminLayout";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
} from "firebase/firestore";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";

// Styled Components
const CategoriesContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: var(--space-xl);
`;

const CategoriesHeader = styled('div')`
  margin-bottom: var(--space-lg);
`;

const PageTitle = styled('h1')`
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 var(--space-sm);
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  gap: var(--space-md);

  svg {
    width: 40px;
    height: 40px;
    color: var(--accent-primary);
  }
`;

const ActionsContainer = styled('div')`
  display: flex;
  gap: var(--space-md);
  margin-bottom: var(--space-xl);
  flex-wrap: wrap;
`;

const DataGridContainer = styled('div')`
  height: 500px;
  margin-top: var(--space-lg);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;

  .MuiDataGrid-root {
    border: none;
    background: transparent;
    color: var(--text-primary);
  }

  .MuiDataGrid-columnHeaders {
    background: var(--bg-tertiary);
    border-bottom: 1px solid var(--border);
  }

  .MuiDataGrid-columnHeader {
    color: var(--text-primary);
    font-weight: 600;
  }

  .MuiDataGrid-row {
    border-bottom: 1px solid var(--border-subtle);

    &:hover {
      background: var(--surface-hover);
    }
  }

  .MuiDataGrid-cell {
    color: var(--text-secondary);
    border-bottom: none;
  }

  .MuiDataGrid-footerContainer {
    background: var(--bg-tertiary);
    border-top: 1px solid var(--border);
    color: var(--text-secondary);
  }
`;

// Dark theme for Material-UI components
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#10b981',
      dark: '#059669',
      light: '#34d399',
    },
    secondary: {
      main: '#06b6d4',
    },
    error: {
      main: '#ef4444',
    },
    success: {
      main: '#22c55e',
    },
    warning: {
      main: '#f59e0b',
    },
    background: {
      default: '#0a0f0a',
      paper: '#2d342d',
    },
    text: {
      primary: '#f0f9f0',
      secondary: '#c8d8c8',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
          },
        },
        outlined: {
          borderColor: '#10b981',
          color: '#10b981',
          '&:hover': {
            borderColor: '#34d399',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#2d342d',
          backgroundImage: 'none',
          border: '1px solid #374151',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#10b981',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#10b981',
            },
          },
        },
      },
    },
  },
});

function Categories() {
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  const fetchCategories = async () => {
    const querySnapshot = await getDocs(collection(db, "categories"));
    const data = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setCategories(data);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const generateCustomCategoryId = async () => {
    const snapshot = await getDocs(collection(db, "categories"));
    const categoryNumbers = snapshot.docs
      .map((doc) => doc.id.match(/^category(\d+)$/))
      .filter(Boolean)
      .map((match) => parseInt(match[1]))
      .sort((a, b) => a - b);
    const nextIdNumber = categoryNumbers.length > 0 ? categoryNumbers[categoryNumbers.length - 1] + 1 : 1;
    return `category${nextIdNumber}`;
  };

  const handleSave = async () => {
    const snapshot = await getDocs(collection(db, "categories"));
    const existing = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    const duplicate = existing.find(
      (cat) =>
        cat.id !== editId &&
        cat.name.trim().toLowerCase() === formData.name.trim().toLowerCase()
    );
    if (duplicate) {
      alert("Category name already exists.");
      return;
    }

    if (editId) {
      await updateDoc(doc(db, "categories", editId), formData);
    } else {
      const newId = await generateCustomCategoryId();
      await setDoc(doc(db, "categories", newId), formData);
    }

    fetchCategories();
    setOpen(false);
    setFormData({ name: "", description: "" });
    setEditId(null);
  };

  const handleEdit = (category) => {
    setFormData({ name: category.name, description: category.description });
    setEditId(category.id);
    setOpen(true);
  };

  const confirmDelete = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (deleteId) {
      await deleteDoc(doc(db, "categories", deleteId));
      fetchCategories();
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  const columns = [
    { field: "name", headerName: "Category Name", flex: 1, disableColumnMenu: true },
    { field: "description", headerName: "Description", flex: 2, disableColumnMenu: true },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <>
          <IconButton color="primary" size="small" onClick={() => handleEdit(params.row)}>
            <EditIcon />
          </IconButton>
          <IconButton color="error" size="small" onClick={() => confirmDelete(params.row.id)}>
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <ThemeProvider theme={darkTheme}>
      <AdminLayout>
        <CategoriesContainer>
          <CategoriesHeader>
            <PageTitle>
              <CategoryIcon />
              Category Management
            </PageTitle>
          </CategoriesHeader>

          <ActionsContainer>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
            >
              Add Category
            </Button>
          </ActionsContainer>

          <DataGridContainer>
            <DataGrid
              rows={categories}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[5, 10, 25]}
              getRowId={(row) => row.id}
              disableColumnMenu
              disableRowSelectionOnClick
              sx={{
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'var(--surface-hover)',
                },
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
              }}
            />
          </DataGridContainer>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{editId ? "Edit Category" : "Add Category"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Category Name"
            name="name"
            fullWidth
            value={formData.name}
            onChange={handleChange}
            margin="dense"
          />
          <TextField
            label="Description"
            name="description"
            fullWidth
            value={formData.description}
            onChange={handleChange}
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {editId ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this category?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
        </CategoriesContainer>
      </AdminLayout>
    </ThemeProvider>
  );
}

export default Categories;
